import { ArticleList } from "@babackapp/customer-ui/components/article";
import { SectionHeader } from "@babackapp/customer-ui/components/header";
import {
  ReceiptLine,
  ReceiptLineLabel,
  ReceiptLineValue,
} from "@babackapp/customer-ui/components/receipt-line";
import { useCustomText } from "@babackapp/return-widget-components/hooks/use-custom-text";
import type { SearchReturnRequestsResponseDto } from "@babackapp/private-api-client";
import { Separator } from "@babackapp/ui/components/separator";
import { useLineItemImages } from "../../hooks/use-line-item-images";
import { ReturnRequestLineItem } from "./return-request-line-item";

type ReturnRequest = SearchReturnRequestsResponseDto["returnRequests"][number];

type Props = {
  returnRequest: ReturnRequest;
};

function formatAmount(amount: number, currency: string): string {
  return new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
  }).format(amount);
}

export function ReturnItemsSummary({ returnRequest }: Props) {
  const t = useCustomText();
  const lineItems = useLineItemImages(returnRequest);

  const exchangeItems = lineItems.filter(
    (li) => li.resolution === "exchange" || li.resolution === "exchange-credit" || li.resolution === "new-articles",
  );
  const refundItems = lineItems.filter((li) => li.resolution === "refund");
  const discountItems = lineItems.filter((li) => li.resolution === "discount");

  const fi = returnRequest.financial_informations;
  const currency = fi.refund.currency || returnRequest.currency.shopCurrency;
  const refundAmount = fi.refund.presentmentAmount;
  const discountAmount = fi.discount.presentmentAmount;
  const rebuyAmount = fi.rebuy.presentmentAmount;
  const exchangeCreditAmount = fi.exchangeCredit?.presentmentAmount ?? 0;

  const hasFinancials = refundAmount > 0 || discountAmount > 0 || rebuyAmount > 0 || exchangeCreditAmount > 0;

  return (
    <div className="flex flex-col gap-6 w-full">
      {exchangeItems.length > 0 && (
        <div className="flex flex-col gap-2">
          <SectionHeader>{t("summary.exchangeItems")}</SectionHeader>
          <ArticleList size="sm" variants={{}}>
            {exchangeItems.map((li) => (
              <ReturnRequestLineItem
                key={li.line_item_id}
                lineItem={li}
                imageLayoutId={`${returnRequest.id}${li.line_item_id}${li.image}`}
              />
            ))}
          </ArticleList>
        </div>
      )}

      {refundItems.length > 0 && (
        <div className="flex flex-col gap-2">
          <SectionHeader>{t("summary.refundItems")}</SectionHeader>
          <ArticleList size="sm" variants={{}}>
            {refundItems.map((li) => (
              <ReturnRequestLineItem
                key={li.line_item_id}
                lineItem={li}
                imageLayoutId={`${returnRequest.id}${li.line_item_id}${li.image}`}
              />
            ))}
          </ArticleList>
        </div>
      )}

      {discountItems.length > 0 && (
        <div className="flex flex-col gap-2">
          <SectionHeader>{t("summary.discountItems")}</SectionHeader>
          <ArticleList size="sm" variants={{}}>
            {discountItems.map((li) => (
              <ReturnRequestLineItem
                key={li.line_item_id}
                lineItem={li}
                imageLayoutId={`${returnRequest.id}${li.line_item_id}${li.image}`}
              />
            ))}
          </ArticleList>
        </div>
      )}

      {hasFinancials && (
        <>
          <Separator />
          <div className="flex flex-col gap-3">
            {refundAmount > 0 && (
              <ReceiptLine>
                <ReceiptLineLabel>{t("financial.refundTotal")}</ReceiptLineLabel>
                <ReceiptLineValue>{formatAmount(refundAmount, currency)}</ReceiptLineValue>
              </ReceiptLine>
            )}
            {discountAmount > 0 && (
              <ReceiptLine>
                <ReceiptLineLabel>{t("financial.discount")}</ReceiptLineLabel>
                <ReceiptLineValue>{formatAmount(discountAmount, currency)}</ReceiptLineValue>
              </ReceiptLine>
            )}
            {rebuyAmount > 0 && (
              <ReceiptLine>
                <ReceiptLineLabel>{t("financial.exchange")}</ReceiptLineLabel>
                <ReceiptLineValue>{formatAmount(rebuyAmount, currency)}</ReceiptLineValue>
              </ReceiptLine>
            )}
            {exchangeCreditAmount > 0 && (
              <ReceiptLine>
                <ReceiptLineLabel>{t("financial.exchangeCredit")}</ReceiptLineLabel>
                <ReceiptLineValue>{formatAmount(exchangeCreditAmount, currency)}</ReceiptLineValue>
              </ReceiptLine>
            )}
          </div>
        </>
      )}
    </div>
  );
}
